import {
  WATERMARK_CONTENT_TYPES,
  WATERMARK_TILE_PRESETS,
  applyWatermarkContentType,
} from "../../features/tools/watermark/Popup/watermarkSettings";

const STYLE_LABELS = {
  [WATERMARK_CONTENT_TYPES.TEXT]: "Text",
  [WATERMARK_CONTENT_TYPES.LOGO]: "Logo",
  [WATERMARK_CONTENT_TYPES.SIGNATURE]: "Signature",
};

const TYPE_LABELS = {
  single: "Single",
  grid: "Grid",
  diamond: "Tile",
};

const POSITION_LABELS = {
  "top-left": "Top left",
  top: "Top center",
  "top-right": "Top right",
  "middle-left": "Middle left",
  center: "Center",
  "middle-right": "Middle right",
  "bottom-left": "Bottom left",
  bottom: "Bottom center",
  "bottom-right": "Bottom right",
};

/**
 * Tile mode label for the preset card corner badge (replaces static badge).
 */
export function getWatermarkPresetBadge(preset) {
  const tile = preset?.patch?.tile;
  return TYPE_LABELS[tile] || TYPE_LABELS.single;
}

/**
 * Build ordered preset summary lines from a job-preset patch:
 * Style → Position → Angle → Opacity (one field per line).
 * Tile mode is shown as the corner badge via getWatermarkPresetBadge().
 * Preset title stays on the card header, not in this list.
 */
export function getWatermarkPresetFeatureLines(preset) {
  const patch = preset?.patch || {};
  const style =
    STYLE_LABELS[patch.contentType] ||
    STYLE_LABELS[WATERMARK_CONTENT_TYPES.TEXT];
  const position = POSITION_LABELS[patch.position] || POSITION_LABELS.center;

  const angle = Number.isFinite(Number(patch.rotation))
    ? Math.round(Number(patch.rotation))
    : 0;
  const opacity = Number.isFinite(Number(patch.opacity))
    ? Math.round(Number(patch.opacity))
    : 100;

  return [
    `Style: ${style}`,
    `Position: ${position}`,
    `Angle: ${angle}`,
    `Opacity: ${opacity}%`,
  ];
}

/**
 * Job-based watermark presets (config only — not fixed artwork).
 * Size/opacity/margin map to current watermarkSettings scales.
 * Feature lines are derived via getWatermarkPresetFeatureLines().
 */
export const WATERMARK_JOB_PRESETS = [
  {
    id: "simple_logo",
    title: "Simple Logo",
    category: "Popular",
    badge: "Default",
    preview: "logo-br",
    patch: {
      contentType: WATERMARK_CONTENT_TYPES.LOGO,
      tile: "single",
      position: "bottom-right",
      size: 1.7,
      opacity: 70,
      rotation: 0,
      spacing: 0,
      margin: 0.04,
      color: "#FFFFFF",
      effect: "none",
      offsetX: 0,
      offsetY: 0,
    },
  },
  {
    id: "subtle_brand",
    title: "Subtle Brand",
    category: "Popular",
    badge: "Ecommerce",
    preview: "logo-subtle",
    patch: {
      contentType: WATERMARK_CONTENT_TYPES.LOGO,
      tile: "single",
      position: "bottom-right",
      size: 1.4,
      opacity: 35,
      rotation: 0,
      spacing: 0,
      margin: 0.04,
      color: "#FFFFFF",
      effect: "none",
      offsetX: 0,
      offsetY: 0,
    },
  },
  {
    id: "logo_website",
    title: "Logo + Website",
    category: "Popular",
    badge: "URL",
    preview: "url-bc",
    patch: {
      contentType: WATERMARK_CONTENT_TYPES.TEXT,
      text: "www.yourbrand.com",
      fontId: "Arial",
      tile: "single",
      position: "bottom",
      size: 2.2,
      opacity: 60,
      rotation: 0,
      spacing: 0,
      margin: 0.05,
      color: "#FFFFFF",
      effect: "shadow",
      offsetX: 0,
      offsetY: 0,
    },
  },
  {
    id: "copyright",
    title: "Copyright",
    category: "Protection",
    badge: "Tiled",
    preview: "tile-copy",
    patch: {
      contentType: WATERMARK_CONTENT_TYPES.TEXT,
      text: "© Your Brand",
      fontId: "Arial",
      tile: "diamond",
      position: "center",
      size: 1.2,
      opacity: 30,
      rotation: -25,
      spacing: 0.55,
      margin: 0.06,
      color: "#FFFFFF",
      effect: "none",
      offsetX: 0,
      offsetY: 0,
    },
  },
  {
    id: "photo_proof",
    title: "Photo Proof",
    category: "Protection",
    badge: "PROOF",
    preview: "tile-proof",
    patch: {
      contentType: WATERMARK_CONTENT_TYPES.TEXT,
      text: "PROOF",
      fontId: "Arial",
      tile: "diamond",
      position: "center",
      size: 1.35,
      opacity: 35,
      rotation: -25,
      spacing: 0.5,
      margin: 0.06,
      color: "#FFFFFF",
      effect: "none",
      offsetX: 0,
      offsetY: 0,
    },
  },
  {
    id: "confidential",
    title: "Confidential",
    category: "Protection",
    badge: "Tiled",
    preview: "tile-conf",
    patch: {
      contentType: WATERMARK_CONTENT_TYPES.TEXT,
      text: "CONFIDENTIAL",
      fontId: "Arial",
      tile: "diamond",
      position: "center",
      size: 1.4,
      opacity: 25,
      rotation: -30,
      spacing: 0.5,
      margin: 0.06,
      color: "#E53935",
      effect: "none",
      offsetX: 0,
      offsetY: 0,
    },
  },
  {
    id: "sample",
    title: "Sample",
    category: "Protection",
    badge: "Preview",
    preview: "tile-sample",
    patch: {
      contentType: WATERMARK_CONTENT_TYPES.TEXT,
      text: "SAMPLE",
      fontId: "Impact",
      tile: "diamond",
      position: "center",
      size: 1.5,
      opacity: 25,
      rotation: -30,
      spacing: 0.48,
      margin: 0.06,
      color: "#FFFFFF",
      effect: "none",
      offsetX: 0,
      offsetY: 0,
    },
  },
  {
    id: "draft",
    title: "Draft",
    category: "Protection",
    badge: "Tiled",
    preview: "tile-draft",
    patch: {
      contentType: WATERMARK_CONTENT_TYPES.TEXT,
      text: "DRAFT",
      fontId: "Arial",
      tile: "diamond",
      position: "center",
      size: 1.45,
      opacity: 25,
      rotation: -30,
      spacing: 0.5,
      margin: 0.06,
      color: "#FFFFFF",
      effect: "none",
      offsetX: 0,
      offsetY: 0,
    },
  },
  {
    id: "dont_copy",
    title: "Don't Copy",
    category: "Protection",
    badge: "Tiled",
    preview: "tile-nocopy",
    patch: {
      contentType: WATERMARK_CONTENT_TYPES.TEXT,
      text: "DO NOT COPY",
      fontId: "Arial",
      tile: "diamond",
      position: "center",
      size: 1.25,
      opacity: 30,
      rotation: -25,
      spacing: 0.52,
      margin: 0.06,
      color: "#FFFFFF",
      effect: "none",
      offsetX: 0,
      offsetY: 0,
    },
  },
  {
    id: "confidential_stamp",
    title: "Confidential",
    category: "Protection",
    badge: "Single",
    preview: "stamp-conf",
    patch: {
      contentType: WATERMARK_CONTENT_TYPES.TEXT,
      text: "CONFIDENTIAL",
      fontId: "Arial",
      tile: "single",
      position: "center",
      size: 4.2,
      opacity: 28,
      rotation: -30,
      spacing: 0,
      margin: 0.08,
      color: "#E53935",
      effect: "none",
      offsetX: 0,
      offsetY: 0,
    },
  },
  {
    id: "draft_stamp",
    title: "Draft",
    category: "Protection",
    badge: "Single",
    preview: "stamp-draft",
    patch: {
      contentType: WATERMARK_CONTENT_TYPES.TEXT,
      text: "DRAFT",
      fontId: "Impact",
      tile: "single",
      position: "center",
      size: 5,
      opacity: 28,
      rotation: -30,
      spacing: 0,
      margin: 0.08,
      color: "#FFFFFF",
      effect: "none",
      offsetX: 0,
      offsetY: 0,
    },
  },
  {
    id: "copyright_stamp",
    title: "Copyright",
    category: "Protection",
    badge: "Single",
    preview: "stamp-copy",
    patch: {
      contentType: WATERMARK_CONTENT_TYPES.TEXT,
      text: "© Your Brand",
      fontId: "Arial",
      tile: "single",
      position: "center",
      size: 3.8,
      opacity: 32,
      rotation: -25,
      spacing: 0,
      margin: 0.08,
      color: "#FFFFFF",
      effect: "none",
      offsetX: 0,
      offsetY: 0,
    },
  },
  {
    id: "dont_copy_stamp",
    title: "Don't Copy",
    category: "Protection",
    badge: "Single",
    preview: "stamp-nocopy",
    patch: {
      contentType: WATERMARK_CONTENT_TYPES.TEXT,
      text: "DO NOT COPY",
      fontId: "Arial",
      tile: "single",
      position: "center",
      size: 3.6,
      opacity: 30,
      rotation: -25,
      spacing: 0,
      margin: 0.08,
      color: "#FFFFFF",
      effect: "none",
      offsetX: 0,
      offsetY: 0,
    },
  },
  {
    id: "ecommerce_product",
    title: "Ecommerce Product",
    category: "Business",
    badge: "Product",
    preview: "logo-ecom",
    patch: {
      contentType: WATERMARK_CONTENT_TYPES.LOGO,
      tile: "single",
      position: "bottom-right",
      size: 1.55,
      opacity: 50,
      rotation: 0,
      spacing: 0,
      margin: 0.04,
      color: "#FFFFFF",
      effect: "none",
      offsetX: 0,
      offsetY: 0,
    },
  },
];

export const DEFAULT_WATERMARK_PRESET_ID = "simple_logo";

export function getWatermarkJobPreset(id) {
  return (
    WATERMARK_JOB_PRESETS.find((p) => p.id === id) ||
    WATERMARK_JOB_PRESETS.find((p) => p.id === DEFAULT_WATERMARK_PRESET_ID)
  );
}

/**
 * Apply a job preset onto current settings.
 * Keeps logo assets; only overwrites text when the preset supplies text.
 */
export function applyWatermarkJobPreset(settings, presetOrId) {
  const preset =
    typeof presetOrId === "string"
      ? getWatermarkJobPreset(presetOrId)
      : presetOrId;
  if (!preset?.patch) return settings;

  const patch = { ...preset.patch };
  let base = settings;
  if (
    patch.contentType &&
    patch.contentType !== (settings.contentType || WATERMARK_CONTENT_TYPES.TEXT)
  ) {
    base = applyWatermarkContentType(settings, patch.contentType);
  }

  const next = {
    ...base,
    ...patch,
    presetId: preset.id,
    offsetX: 0,
    offsetY: 0,
    byMode: base.byMode || settings.byMode || {},
  };

  // Preserve uploaded logo when switching between logo presets.
  if (patch.contentType === WATERMARK_CONTENT_TYPES.LOGO) {
    next.logoUrl = settings.logoUrl ?? null;
    next.logoNaturalWidth = settings.logoNaturalWidth || 0;
    next.logoNaturalHeight = settings.logoNaturalHeight || 0;
  }

  // Don't wipe custom text if preset didn't define one.
  if (patch.text == null && base.text != null) {
    next.text = base.text;
  }

  // Keep embed preference.
  next.embed = Boolean(settings.embed);

  // Ensure spacing exists for tile modes.
  if (next.tile === "single") {
    next.spacing = Number(next.spacing) || 0;
  } else if (next.spacing == null) {
    next.spacing = WATERMARK_TILE_PRESETS[next.tile]?.spacing ?? 0.5;
  }

  return next;
}
